import {
  FieldSchema,
  GridFilterTypes,
  Schema,
  SchemaClient,
  prepareEquipmentSchema
} from "@digitallab/grid-common-lib";

/**
 * digital-log-book-frontend's equipment schema: the shared equipment schema, plus the handful of
 * places logbook deliberately behaves differently.
 *
 * Why a local extension rather than hand-written flag lists: `Schema` is an open
 * `Record<string, FieldSchema>`, so a consumer can override fields freely. That keeps every grid
 * flag derived from one place instead of spread across nine `listOf…` arrays in `constants.tsx`
 * that have to be kept in step by hand.
 *
 * Unlike booking-frontend, logbook declares **no app-only fields** — every one of the 53 columns it
 * shows already exists in the shared schema, dimension columns included.
 */

/**
 * Which columns the grid shows before a user has saved a layout of their own.
 *
 * This is the set the hand-written `DEFAULT_COLUMNS` showed, so the default view is unchanged by the
 * move to a derived column list. Ordering still comes from the schema.
 *
 * The shared schema's own `grid.defaultVisible` happens to agree with this today. Pinned anyway, and
 * applied to every field in both directions: `defaultVisible` was authored for
 * instrument-repository's default view, and a change there must not silently move logbook's.
 */
const LOGBOOK_DEFAULT_VISIBLE = ["manufacturer", "materialNumber", "serialNumber", "siteName"];

/** Overwrites one grid property on a field, leaving the rest of the aspect intact. */
const overrideGrid = (field: FieldSchema | undefined, grid: Record<string, unknown>): FieldSchema =>
  ({ ...field, grid: { ...(field?.grid ?? {}), ...grid } }) as FieldSchema;

/**
 * Where logbook diverges from the shared schema on a column both apps have.
 *
 * `siteName`: the shared schema filters it as a set, and instrument-repository offers that filter.
 * Logbook does not — `listOfFieldsNoFilter` has suppressed it for as long as the list has existed,
 * which makes sense in an app already scoped to the user's site. Preserved deliberately; removing
 * this override would add a site filter to both logbook equipment grids.
 *
 * Visibility: every field is forced to its logbook default, in both directions. Setting only the
 * visible ones would leave anything the shared schema defaults to visible on, whether logbook shows
 * it or not.
 */
const applyLogbookOverrides = (schema: Schema): Schema => {
  const overridden: Schema = { ...schema };

  Object.keys(overridden).forEach((name) => {
    const field = overridden[name];
    if (!field?.grid) {
      return;
    }
    overridden[name] = overrideGrid(field, {
      defaultVisible: LOGBOOK_DEFAULT_VISIBLE.includes(name)
    });
  });

  overridden.siteName = overrideGrid(overridden.siteName, { filter: GridFilterTypes.NONE });

  return overridden;
};

/**
 * Builds the schema the logbook equipment grids derive their columns from.
 *
 * @param client - Read client for the master data the shared schema resolves its enums from.
 * @returns The shared equipment schema, overridden for logbook.
 */
export const prepareLogbookEquipmentSchema = async (client: SchemaClient): Promise<Schema> => {
  const equipmentSchema = await prepareEquipmentSchema(client);
  return applyLogbookOverrides(equipmentSchema);
};

/** Exported for the characterization test, which asserts the default view against the constants. */
export { LOGBOOK_DEFAULT_VISIBLE };
